import React, { useState } from 'react';
import { BACKEND_URL } from '../config';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 Mo
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const ProjectImageUpload = ({ projectId, onUpload, onFeedback }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = e => {
    const selectedFile = e.target.files[0];
    setError('');
    setSuccess(false);
    
    if (!selectedFile) {
      setFile(null);
      setPreview(null);
      return;
    }
    
    // Vérifier le type
    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError('Format non supporté. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }
    
    // Vérifier la taille
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`Image trop volumineuse. Max: ${MAX_FILE_SIZE / 1024 / 1024} Mo`);
      return;
    }
    
    setFile(selectedFile);
    
    // Créer un aperçu
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async e => {
    e.preventDefault();
    if (!file) return setError('Aucun fichier sélectionné');
    if (!projectId) return setError('ID projet manquant');
    
    setUploading(true);
    setError('');
    setSuccess(false);
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('project_id', projectId);
    
    try {
      // Utiliser l'endpoint direct pour l'upload (contourne le front controller)
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/upload.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Erreur upload');
      }
      
      setSuccess(true);
      setError('');
      setFile(null);
      setPreview(null);
      if (onUpload) onUpload();
      if (onFeedback) onFeedback({ success: true, message: 'Image de couverture mise à jour avec succès !' });
      
      // Masquer le message de succès après 5 secondes
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.message || 'Erreur lors de l\'upload';
      setError(errorMsg);
      setSuccess(false);
      if (onFeedback) onFeedback({ success: false, message: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Info bulle */}
      <div className="text-xs text-red-800 mb-2 flex flex-wrap gap-2">
        <span className="bg-gray-100 px-2 py-1 rounded">📏 Max: 5 Mo</span>
        <span className="bg-gray-100 px-2 py-1 rounded">📄 JPG, PNG, GIF, WebP</span>
        <span className="bg-orange-100 px-2 py-1 rounded">🖼️ Vous pouvez ajouter des images / photos de votre projet</span>
      </div>
      
      <form onSubmit={handleUpload} className="flex flex-col gap-3">
        {/* Aperçu */}
        {preview && (
          <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-indigo-200">
            <img src={preview} alt="Aperçu" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => { setFile(null); setPreview(null); }}
              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
            >
              ✕
            </button>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <label className="flex-1 cursor-pointer">
            <span className="sr-only">Choisir une image</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleChange}
              className="block w-full text-sm text-gray-600 
                file:mr-3 file:py-2 file:px-4 
                file:rounded-lg file:border-0 
                file:text-sm file:font-semibold 
                file:bg-indigo-50 file:text-indigo-700 
                hover:file:bg-indigo-100 
                file:cursor-pointer
                transition"
            />
          </label>
          <button 
            type="submit" 
            disabled={!file || uploading}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 rounded-lg transition text-sm whitespace-nowrap flex items-center gap-2"
          >
            {uploading ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Upload en cours...
              </>
            ) : (
              <>📤 Uploader</>
            )}
          </button>
        </div>
        
        {/* Messages de feedback */}
        {success && (
          <div className="flex items-center gap-2 text-green-700 text-sm bg-green-100 border border-green-300 px-4 py-3 rounded-xl animate-pulse">
            <span className="text-xl">✅</span>
            <div>
              <p className="font-semibold">Image uploadée avec succès !</p>
              <p className="text-green-600 text-xs">L'image a été ajoutée à la galerie de votre projet.</p>
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-red-700 text-sm bg-red-100 border border-red-300 px-4 py-3 rounded-xl">
            <span className="text-xl">❌</span>
            <div>
              <p className="font-semibold">Échec de l'upload. Veuillez vérifier le fichier, la taille et réessayer.</p>
              <p className="text-red-600 text-xs">{error}</p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};
export default ProjectImageUpload;
